/*
 * The meter — a needle on an arc for temperature, rpm, pressure, level: a
 * configurable range, unit, tone bands and a full or half arc. Distinct from the
 * catalog's Dial (the fixed 240° face with warn/danger tinting) in what it takes
 * as config, so the two are named differently on purpose. Lives here so the
 * catalog can carry it without importing the ideas registry.
 */
import { KIT_V1_HEADER } from '../runtime/kit';

export const METER = KIT_V1_HEADER + `
const meta = { title: 'Meter', minWidth: 120, minHeight: 110, fps: 30, aspect: '1:1', preferredW: 3, settings: SYM.concat([SET.num('min', 'Min'), SET.num('max', 'Max'), SET.decimals, SET.pick('arc', 'Arc', ['240', '180']), SET.num('ticks', 'Ticks')]) };
function setup(ctx) {
  const c = ctx.config;
  const read = ctx.sub(c.controllerId, c.symbolId);
  const lo = Number(c.min) || 0, hi = Number(c.max) || 100;
  const unit = c.unit == null ? '' : String(c.unit);
  const dec = c.decimals == null ? 0 : Number(c.decimals);
  const label = String(c.label || c.symbolId || '');
  const sweep = Number(c.arc) === 180 ? 180 : 240;
  const bands = (Array.isArray(c.bands) ? c.bands : []).map((bd) => ({ max: Number(bd.max), tok: '--' + (bd.tone || 'amber') }));
  const nTicks = Math.max(2, Math.min(12, Number(c.ticks) || 5));
  let shown = null;
  return function () {
    const b = ctx.size();
    const v = read().num;
    if (v !== null) shown = shown === null ? v : shown + (v - shown) * 0.18;
    const M = 10, labelH = 16;
    const S = Math.min(b.width - 2 * M, (b.height - labelH - 2 * M) * (sweep === 180 ? 1.7 : 1.15));
    const R = S / 2, cx = b.width / 2, cy = M + (sweep === 180 ? R : R * 0.98);
    const a0 = -sweep / 2, a1 = sweep / 2;
    const ang = (x) => a0 + clamp((x - lo) / (hi - lo), 0, 1) * (a1 - a0);
    const nodes = [];
    const w = clamp(R * 0.11, 4, 10);
    nodes.push(el('path', { d: arc(cx, cy, R - w / 2, a0, a1), fill: 'none', stroke: '--border-color-light', 'stroke-width': w, 'stroke-linecap': 'round' }));
    if (shown !== null) nodes.push(el('path', { d: arc(cx, cy, R - w / 2, a0, Math.max(a0 + 0.5, ang(shown))), fill: 'none', stroke: '--blue', 'stroke-width': w, 'stroke-linecap': 'round', tip: [T(label, (v === null ? '--' : num(v, dec)) + (unit ? ' ' + unit : ''), '--blue'), T('range', num(lo, 0) + ' – ' + num(hi, 0) + (unit ? ' ' + unit : ''))] }));
    // Bands: thin arcs OUTSIDE the main arc, in their tone.
    let from = lo;
    for (const bd of bands) { if (bd.max > from) nodes.push(el('path', { d: arc(cx, cy, R + 3, ang(from), ang(Math.min(bd.max, hi))), fill: 'none', stroke: bd.tok, 'stroke-width': 2 })); from = bd.max; }
    for (let i = 0; i <= nTicks; i++) {
      const x = lo + (i / nTicks) * (hi - lo), a = ang(x);
      const p1 = polar(cx, cy, R - w - 3, a), p2 = polar(cx, cy, R - w - 8, a);
      nodes.push(el('line', { x1: p1[0], y1: p1[1], x2: p2[0], y2: p2[1], stroke: '--chart-axis', 'stroke-width': 1 }));
      if (R >= 44 && (i === 0 || i === nTicks)) { const p = polar(cx, cy, R + 4, a); nodes.push(txt(p[0], p[1] + 12, num(x, 0), { 'text-anchor': 'middle', fill: '--chart-axis-label' })); }
      else if (R >= 56 && nTicks <= 6) { const p = polar(cx, cy, R - w - 18, a); nodes.push(txt(p[0], p[1] + 3.5, num(x, 0), { 'text-anchor': 'middle', fill: '--chart-axis-label' })); }
    }
    if (shown !== null) { const p = polar(cx, cy, R - w - 4, ang(shown)); nodes.push(el('line', { x1: cx, y1: cy, x2: p[0], y2: p[1], stroke: '--text-primary', 'stroke-width': 2, 'stroke-linecap': 'round' })); }
    nodes.push(el('circle', { cx: cx, cy: cy, r: clamp(R * 0.06, 3, 6), fill: '--text-primary' }));
    const big = clamp(R * 0.42, 14, 34);
    const vy = sweep === 180 ? cy - 6 : cy + R * 0.55;
    const valS = v === null ? '--' : num(v, dec);
    nodes.push(txt(cx - (unit ? tw(unit, big * 0.4) / 2 + 2 : 0), vy, valS, { 'font-size': big, 'font-weight': 600, fill: '--text-primary', 'text-anchor': 'middle' }));
    if (unit && v !== null) nodes.push(txt(cx + tw(valS, big) / 2 + 4, vy, unit, { 'font-size': Math.max(10, big * 0.4), 'font-weight': 500 }));
    nodes.push(txt(cx, b.height - 6, fit(label, b.width - 2 * M, 11), { 'font-size': 11, 'font-weight': 500, 'text-anchor': 'middle' }));
    return root(b, nodes);
  };
}
return { meta, setup };`;
